import { useEffect, useMemo, useRef, useState } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import { useNavigate, useLocation } from "react-router-dom";
import { fromState } from "@/lib/nav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { showUploadFailure } from "@/lib/uploadError";
import { AuthImg } from "@/components/AuthImg";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Camera, Loader2, Truck, Boxes, Archive, ChevronUp, ChevronDown, Image as ImageIcon, ArrowLeft, Search, CheckCircle2, Package, SkipForward, AlertTriangle, Clock , Maximize2} from "lucide-react";
import { SearchCameraButton } from "@/components/search/engine";
import { useOwnTag } from "@/components/search/SearchBox";
import { usePhotoSources, CHOOSE_GALLERY } from "@/components/PhotoSourceButtons";

const emailPrefix = (e) => (e || "").split("@")[0] || "someone";
const timeRange = (times) => {
  const t = (times || []).filter(Boolean);
  if (!t.length) return "";
  return t.length === 1 ? t[0] : `${t[0]} – ${t[t.length - 1]}`;
};

const STATUS_RANK = { dismantling: 0, partially_dismantled: 1, fully_dismantled: 2, whole: 3, awaiting: 4, open: 5, closed: 9 };
const statusStyle = (s) => ({
  dismantling: "bg-[#E0781A] text-white", partially_dismantled: "bg-amber-500 text-white",
  fully_dismantled: "bg-amber-700 text-white", whole: "bg-slate-500 text-white", awaiting: "bg-slate-400 text-white",
  open: "bg-emerald-600 text-white", closed: "bg-slate-300 text-slate-700",
}[s] || "bg-slate-400 text-white");

const TypeCell = ({ g }) => {
  if (g.group_type === "donor") return <span className="inline-flex items-center gap-1.5 text-slate-600"><Truck className="w-4 h-4 text-[#B01E28]" />Donor truck</span>;
  if (g.kind === "legacy") return <span className="inline-flex items-center gap-1.5 text-[#5E3A93]"><Archive className="w-4 h-4" />Legacy lot</span>;
  return <span className="inline-flex items-center gap-1.5 text-[#C76714]"><Boxes className="w-4 h-4" />Standard lot</span>;
};

export default function DismantlePhotosPage() {
  const [groups, setGroups] = useState([]);
  const [shot, setShot] = useState(null);   // the one photo viewer
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);
  const [includeDone, setIncludeDone] = useBackState("includeDone", false);
  const [sort, setSort] = useBackState("sort", { key: "needs", dir: "desc" });
  const [search, setSearch] = useBackState("search", "");
  const [asked, setAsked] = useState("");   // what the server last searched — S4: the box is on the one engine
  useEffect(() => {
    const t = setTimeout(() => setAsked(search.trim()), search.trim() ? 300 : 0);
    return () => clearTimeout(t);
  }, [search]);
  const [selected, setSelected] = useState(null);   // open group → upload workspace
  const [pool, setPool] = useState({ total: 0, unmatched: 0 });
  const [dumping, setDumping] = useState(false);
  const [dumpCount, setDumpCount] = useState(0);
  const [dumpDone, setDumpDone] = useState(0);
  const [sessionId, setSessionId] = useState("");   // one capture session per workspace visit
  const [partCount, setPartCount] = useState(1);     // how many parts started this session
  const [activePart, setActivePart] = useState(0);   // which part new dumps attach to (0-indexed)
  const [poolPhotos, setPoolPhotos] = useState([]);  // full pool for the "Photos on this lot" panel
  const abortRef = useRef(null);                     // cancels in-flight chunks on back-out

  const loadPool = async (g) => {
    try {
      const { data } = await api.get(`/groups/${g.group_type}/${g.group_id}/photos`);
      const ph = data.photos || [];
      setPoolPhotos(ph);
      setPool({ total: ph.length, unmatched: ph.filter((p) => !p.assigned).length });
      return ph;
    } catch { setPoolPhotos([]); setPool({ total: 0, unmatched: 0 }); return []; }
  };

  const dumpToLot = async (files) => {
    const arr = Array.from(files);
    if (!arr.length || !selected) return;
    await runDump(arr, false, sessionId, activePart);
  };
  const photo = usePhotoSources({ onFiles: dumpToLot, disabled: dumping, testidPrefix: "pu" });

  const runDump = async (arr, isRetry, sessId, partGroup) => {
    if (!arr.length || !selected) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setDumping(true);
    setDumpCount(arr.length);
    setDumpDone(0);
    // Snapshot the TRUE server-side count before we start. We reconcile against this
    // afterwards so a gateway timeout / dropped response (files DID land) can't be
    // mistaken for a failure — which is what made staff re-upload and create duplicates.
    const before = await loadPool(selected);
    const poolBefore = before.length;
    // Upload in small SEQUENTIAL chunks so no single request crosses the gateway
    // timeout or body-size limit. Order is preserved (tag-first SOP).
    const CHUNK = 4;
    let confirmedOk = 0;         // count the server explicitly acknowledged (200)
    const formatFailed = [];     // File objects the SERVER rejected (bad format) — safe to retry
    let uncertainCount = 0;      // files in chunks whose response never arrived (may have landed)
    let aborted = false;
    try {
      for (let i = 0; i < arr.length; i += CHUNK) {
        const slice = arr.slice(i, i + CHUNK);
        const fd = new FormData();
        fd.append("session_id", sessId || "");
        fd.append("part_group", String(partGroup ?? 0));
        // send each file's ABSOLUTE index in the selection as a deterministic order
        // tiebreaker (backend orders by session, then part, then capture-time, then this index).
        slice.forEach((f, j) => { fd.append("files", f); fd.append("indexes", String(i + j)); });
        try {
          const { data } = await api.post(`/groups/${selected.group_type}/${selected.group_id}/photos`, fd, { timeout: 120000, signal: controller.signal });
          confirmedOk += data.count || 0;
          const failedNames = new Set(data.failed || []);
          slice.forEach((f) => { if (failedNames.has(f.name)) formatFailed.push(f); });
        } catch (e) {
          if (controller.signal.aborted) { aborted = true; break; }
          // No response — the server MAY have saved these (timeout after write). Don't
          // mark failed blindly; reconcile against the pool below.
          uncertainCount += slice.length;
        }
        setDumpDone(Math.min(i + CHUNK, arr.length));
      }
      if (aborted) return;
      // ---- RECONCILE: trust the pool, not the exception ----
      const after = await loadPool(selected);
      const landed = Math.max(0, after.length - poolBefore);
      const uncertainLanded = Math.max(0, landed - confirmedOk);
      const uncertainMissing = Math.max(0, uncertainCount - uncertainLanded);
      const totalLanded = confirmedOk + uncertainLanded;
      if (totalLanded > 0) toast.success(`Dumped ${totalLanded} photo${totalLanded === 1 ? "" : "s"} to ${selected.label} · Part ${(partGroup ?? 0) + 1}${isRetry ? " on retry" : ""}`);
      // Format failures are known File objects the server never saved → safe one-tap retry.
      if (formatFailed.length) showUploadFailure(formatFailed, (ff) => runDump(ff, true, sessId, partGroup));
      // Genuine network loss we can't name → let staff re-add ONLY the missing ones using
      // the visual strip below (blindly retrying the whole chunk would duplicate landed photos).
      if (uncertainMissing > 0) {
        toast.warning(`${uncertainMissing} photo${uncertainMissing === 1 ? "" : "s"} may not have uploaded — check "Photos on this lot" below and re-add only the ones that are missing.`, { duration: 12000 });
      }
    } finally {
      abortRef.current = null;
      setDumping(false); setDumpCount(0); setDumpDone(0);
    }
  };

  const load = async (done) => {
    setLoading(true);
    pageRef.current = 1;
    try {
      const { data } = await api.get(`/photo-upload/groups`, { params: { include_done: done ? "true" : "false", page: 1, page_size: 100, q: asked } });
      setGroups(data.groups || []);
      setHasMore(!!data.has_more);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const { data } = await api.get(`/photo-upload/groups`, { params: { include_done: includeDone ? "true" : "false", page: next, page_size: 100, q: asked } });
      setGroups((prev) => [...prev, ...(data.groups || [])]);
      setHasMore(!!data.has_more);
      pageRef.current = next;
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoadingMore(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(includeDone); }, [includeDone, asked]);
  // ENTER AND SCANNING (S4, his rule): one of our part tags opens the part, a truck's own stock # opens that truck's upload workspace; anything else is a list.
  const ownTag = useOwnTag("pu-search");
  const navigate = useNavigate();
  const location = useLocation();
  const onEnter = async (v) => {
    const t = (v || "").trim();
    if (!t) return;
    setSearch(t); setAsked(t);
    const hit = await ownTag(t);
    if (hit?.kind === "part") { navigate(`/parts/${hit.id}`, { state: fromState(location, "Bulk Photo Upload") }); return; }
    if (hit?.kind !== "truck") return;
    const { data } = await api.get("/photo-upload/groups", { params: { include_done: includeDone ? "true" : "false", q: hit.label, page_size: 200 } })
      .catch(() => ({ data: {} }));
    const g = (data.groups || []).find((x) => x.group_type === "donor" && x.group_id === hit.id);
    if (g) openGroup(g);
  };

  const openGroup = (g) => {
    setSelected(g); setPool({ total: 0, unmatched: 0 }); setPoolPhotos([]); loadPool(g);
    setSessionId(crypto?.randomUUID?.() || `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setPartCount(1); setActivePart(0);
  };
  const backToList = () => { abortRef.current?.abort(); setSelected(null); load(includeDone); };

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  const visible = useMemo(() => {
    let arr = groups;
    const val = (g) => ({
      group: `${g.label} ${g.sub_label}`.toLowerCase(), type: `${g.group_type}-${g.kind || ""}`,
      status: STATUS_RANK[g.status] ?? 6, parts: g.staging_count || 0,
      photos: g.pool_photo_count || 0, last: g.last_activity || "",
    }[sort.key]);
    arr = [...arr].sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0));
    return sort.dir === "desc" ? arr.reverse() : arr;
  }, [groups, sort]);

  const SortHead = ({ k, label, className = "" }) => (
    <th data-testid={`pu-sort-${k}`} onClick={() => toggleSort(k)} className={`px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px] text-slate-500 cursor-pointer select-none hover:text-slate-800 ${className}`}>
      <span className="inline-flex items-center gap-1">{label}{sort.key === k && (sort.dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}</span>
    </th>
  );

  // ---------- UPLOAD WORKSPACE ----------
  // Group the pool by (session, part) — the SAME grouping Tag-to-Photos uses — so staff
  // see "is everything for this truck here?" at a glance, and who uploaded each block.
  // poolPhotos arrives already ordered (session → part → capture-time), so Map order holds.
  const poolGroups = useMemo(() => {
    const map = new Map();
    for (const p of poolPhotos) {
      const key = `${p.session_id || "__legacy__"}::${p.part_group ?? 0}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries()).map(([key, photos]) => ({
      key,
      uploader: photos[0]?.uploader,
      part_group: photos[0]?.part_group ?? 0,
      photos,
      count: photos.length,
      matched: photos.filter((p) => p.assigned).length,
      unmatched: photos.filter((p) => !p.assigned).length,
      noTime: photos.filter((p) => p.no_capture_time).length,
      times: photos.map((p) => p.captured_at_display),
    }));
  }, [poolPhotos]);

  if (selected) {
    return (
      <div data-testid="photo-upload-page">
        <button data-testid="pu-back" onClick={backToList} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="w-4 h-4" />All groups</button>
        <div className="flex items-center gap-2 mb-1">
          <h1 className="font-display text-2xl font-bold tracking-tight"><span className="font-mono">{selected.label}</span></h1>
          <Badge className={`rounded-full border-0 text-[10px] font-mono uppercase ${statusStyle(selected.status)}`}>{selected.status}</Badge>
          {selected.kind === "legacy" && <Badge className="rounded-full border-0 bg-[#F6F1FB] text-[#5E3A93] text-[10px] font-mono">LEGACY · $0</Badge>}
        </div>
        <p className="text-sm text-slate-400 mb-3">{selected.sub_label}{selected.vin ? <span className="font-mono text-slate-600 font-semibold"> · VIN {selected.vin}</span> : ""}</p>

        <div data-testid="pu-tagfirst-banner" className="flex items-start gap-2 rounded-lg bg-[#FFF4E6] border border-[#E0781A]/40 px-3 py-2 mb-4 max-w-2xl">
          <Camera className="w-4 h-4 text-[#C76714] mt-0.5 shrink-0" />
          <p className="text-xs text-[#8a4f12]"><span className="font-bold">Shoot each part tag-first, then tap "Start next part" before the next one.</span> Photos stay grouped by part even if you jump back — so they can't get mixed up. Marry them to tags later on Tag-to-Photos.</p>
        </div>

        {/* ---- PART TRACKER: keeps each part's photos in its own contiguous group ---- */}
        <div data-testid="pu-part-tracker" className="max-w-2xl mb-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-[#B01E28]" />Part being photographed</span>
            <Button data-testid="pu-start-next-part" onClick={() => { const n = partCount; setPartCount(n + 1); setActivePart(n); }}
              disabled={dumping} size="sm" className="rounded-lg h-8 text-xs bg-[#B01E28] hover:bg-[#8f1820] text-white">
              <SkipForward className="w-3.5 h-3.5 mr-1" />Start next part
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="pu-part-chips">
            {Array.from({ length: partCount }).map((_, i) => (
              <button key={i} data-testid={`pu-part-chip-${i}`} onClick={() => setActivePart(i)} disabled={dumping}
                className={`rounded-full px-3 py-1 text-xs font-mono font-semibold border transition-colors ${activePart === i ? "border-[#B01E28] bg-[#FDF3F4] text-[#B01E28]" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
                Part {i + 1}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">New photos go to <span className="font-semibold text-[#B01E28]">Part {activePart + 1}</span>. Tap a chip to add more to an earlier part.</p>
        </div>

        {/* ---- PRIMARY: CAMERA-FIRST (capture, single-shot per tap) + secondary library ---- */}
        <div className="max-w-2xl mb-4">
          {photo.inputs}
          <button type="button" data-testid="pu-dump-zone" disabled={dumping} onClick={photo.openCamera} className="block w-full text-left cursor-pointer disabled:cursor-default">
            <div className={`rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${dumping ? "border-[#E0781A] bg-[#FFF4E6]" : "border-[#B01E28]/40 bg-[#FFF9F3] hover:border-[#E0781A] hover:bg-[#FFF4E6]"}`}>
              <div className="flex flex-col items-center gap-2">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#B01E28] text-white">
                  {dumping ? <Loader2 className="w-7 h-7 animate-spin" /> : <Camera className="w-7 h-7" />}
                </span>
                <div data-testid="pu-dump-label" className="font-display text-lg font-bold text-slate-800">{dumping ? `Uploading ${dumpDone} / ${dumpCount} photo${dumpCount === 1 ? "" : "s"}…` : `Photograph Part ${activePart + 1}`}</div>
                <div className="text-xs text-slate-500">Tag first, then the part. Tap to open the camera.</div>
              </div>
            </div>
          </button>
          <button type="button" data-testid="pu-library-trigger" disabled={dumping} onClick={photo.openGallery}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#B01E28] disabled:opacity-50">
            <ImageIcon className="w-4 h-4" />{CHOOSE_GALLERY} (pick many)
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 max-w-2xl mb-6">
          <Badge data-testid="pu-pool-total" className="rounded-full border-0 bg-slate-800 text-white text-[11px] font-mono"><ImageIcon className="w-3 h-3 mr-1" />{pool.total} in pool</Badge>
          <Badge data-testid="pu-pool-unmatched" className="rounded-full border-0 bg-[#E0781A] text-white text-[11px] font-mono">{pool.unmatched} unmatched</Badge>
          {pool.total > 0 && pool.total - pool.unmatched > 0 && <Badge className="rounded-full border-0 bg-emerald-600 text-white text-[11px] font-mono"><CheckCircle2 className="w-3 h-3 mr-1" />{pool.total - pool.unmatched} matched</Badge>}
          <span className="text-xs text-slate-400">Match these to tags later on the Tag-to-Photos screen.</span>
        </div>

        {/* ---- BATCH VISIBILITY: everything on this lot, grouped by session/part ---- */}
        {poolGroups.length > 0 && (
          <div data-testid="pu-batch-panel" className="max-w-2xl space-y-3 pb-10">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><ImageIcon className="w-4 h-4 text-[#B01E28]" />Photos on this lot</h2>
            {poolGroups.map((grp, gi) => (
              <div key={grp.key} data-testid={`pu-batch-group-${gi}`} className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className="rounded-full border-0 bg-slate-800 text-white text-[10px] font-mono">Part {grp.part_group + 1}</Badge>
                    <Badge data-testid={`pu-batch-count-${gi}`} className="rounded-full border-0 bg-[#FDF3F4] text-[#B01E28] text-[10px] font-mono">{grp.count} photo{grp.count === 1 ? "" : "s"}</Badge>
                    {grp.matched > 0 && <Badge data-testid={`pu-batch-matched-${gi}`} className="rounded-full border-0 bg-emerald-600 text-white text-[10px] font-mono"><CheckCircle2 className="w-3 h-3 mr-0.5" />{grp.matched} matched</Badge>}
                    {grp.unmatched > 0 && <Badge className="rounded-full border-0 bg-[#E0781A] text-white text-[10px] font-mono">{grp.unmatched} unmatched</Badge>}
                    {grp.noTime > 0 && <Badge data-testid={`pu-batch-notime-${gi}`} className="rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[10px] font-mono"><AlertTriangle className="w-3 h-3 mr-0.5" />{grp.noTime} no capture time</Badge>}
                  </div>
                </div>
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
                  {grp.photos.map((p, pi) => (
                    <button key={p.file_id} type="button" data-testid={`pu-batch-thumb-${gi}-${pi}`}
                      onClick={() => setShot({ list: grp.photos, idx: pi })}
                      className={`relative aspect-square rounded-md overflow-hidden bg-slate-100 border ${p.assigned ? "border-emerald-400" : "border-slate-200"}`}>
                      <AuthImg fileId={p.file_id} thumb className="w-full h-full object-cover" alt={`photo ${pi + 1}`} />
                      {pi === 0 && <span className="absolute top-0.5 left-0.5 rounded bg-[#B01E28] text-white text-[8px] font-bold px-1 leading-tight">TAG</span>}
                      {p.no_capture_time && <span className="absolute bottom-0.5 right-0.5 rounded-full bg-amber-500 text-white p-0.5"><AlertTriangle className="w-2.5 h-2.5" /></span>}
                    </button>
                  ))}
                </div>
                <div data-testid={`pu-batch-uploader-${gi}`} className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-400">
                  <span>uploaded by <span className="font-medium text-slate-500">{emailPrefix(grp.uploader)}</span></span>
                  {timeRange(grp.times) && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{timeRange(grp.times)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- GROUPS LIST ----------
  return (
    <div data-testid="photo-upload-page">
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2"><Camera className="w-6 h-6 text-[#B01E28]" />Bulk Photo Upload</h1>
      <p className="text-sm text-slate-500 mb-3">Pick the lot you're working, then <span className="font-semibold">dump all its photos into the pool</span> — tag first, no matching needed. You'll marry photos to tags later on the Tag-to-Photos screen.</p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-1.5 w-80 max-w-full">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="pu-search" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnter(e.currentTarget.value); } }} enterKeyHint="search"
              placeholder="Find by stock#, VIN, make/model, lot…" className="pl-9 h-10 rounded-lg border-slate-200" />
          </div>
          <SearchCameraButton testid="pu-search" onScan={onEnter} />
        </div>
        <Badge data-testid="pu-total-pool" className="rounded-full bg-slate-800 text-white border-0">{groups.reduce((n, g) => n + (g.pool_photo_count || 0), 0)} photos uploaded</Badge>
        <button data-testid="pu-toggle-done" onClick={() => setIncludeDone((v) => !v)} className="text-xs font-mono text-slate-500 hover:text-slate-800 underline">
          {includeDone ? "Hide fully-photographed groups" : "Show all active groups"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-10"><Loader2 className="w-4 h-4 animate-spin" />Loading groups…</div>
      ) : visible.length === 0 ? (
        <div data-testid="pu-empty" className="border border-dashed border-slate-300 rounded-xl py-12 text-center text-slate-400 text-sm">{search ? "No groups match your search." : "No groups need photos right now. 🎉"}</div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <SortHead k="group" label="Group" /><SortHead k="type" label="Type" /><SortHead k="status" label="Status / Stage" />
                <SortHead k="parts" label="Parts in lot" /><SortHead k="photos" label="Photos uploaded" /><SortHead k="last" label="Last activity" className="hidden md:table-cell" />
              </tr>
            </thead>
            <tbody>
              {visible.map((g, idx) => (
                <tr key={`${g.group_type}-${g.group_id}-${idx}`} data-testid={`pu-group-row-${g.group_id}`} onClick={() => openGroup(g)} className="border-b border-slate-100 cursor-pointer hover:bg-[#FFF9F3]">
                  <td className="px-3 py-2.5"><div className="font-mono font-semibold text-slate-800">{g.label}</div><div className="text-xs text-slate-400 truncate max-w-[220px]">{g.sub_label}</div>{g.vin ? <div data-testid={`pu-vin-${g.group_id}`} className="font-mono text-[10px] text-slate-600 font-semibold truncate max-w-[220px]">VIN {g.vin}</div> : null}</td>
                  <td className="px-3 py-2.5 text-xs"><TypeCell g={g} /></td>
                  <td className="px-3 py-2.5"><Badge className={`rounded-full border-0 text-[10px] font-mono uppercase ${statusStyle(g.status)}`}>{g.status}</Badge></td>
                  <td className="px-3 py-2.5"><span data-testid={`pu-parts-${g.group_id}`} className="font-mono font-bold text-slate-700">{g.staging_count || 0}</span></td>
                  <td className="px-3 py-2.5"><span data-testid={`pu-photos-${g.group_id}`} className={`font-mono font-bold ${(g.pool_photo_count || 0) > 0 ? "text-emerald-600" : "text-slate-300"}`}>{g.pool_photo_count || 0}</span></td>
                  <td className="px-3 py-2.5 text-xs text-slate-400 font-mono hidden md:table-cell">{(g.last_activity || "").slice(0, 10) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && hasMore && (
        <div className="flex justify-center mt-4">
          <Button data-testid="pu-load-more" variant="outline" className="rounded-lg" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? "Loading…" : "Load more groups"}
          </Button>
        </div>
      )}
      <PhotoLightbox photos={shot?.list || []} index={shot ? shot.idx : null}
        onIndex={(i) => setShot((v) => v && { ...v, idx: i })} onClose={() => setShot(null)}
        testidPrefix="pu-batch-photo-viewer" />
    </div>

  );
}
